import { showToast } from '@/helper/toast.helper';
import {
  AdminSingleUserResponse,
  AdminUserFilterParams,
  AdminUserInfoResponse,
  AdminUserListResponse,
  AdminUsers,
  BlueNumberResponse,
  CreateAdminUser,
  Roles,
  UpdateAdminUser,
  UserLookupResponse,
} from '@/types/adminuser.types';
import { ApiResponse } from '@/types/common';
import axios from 'axios';
import api from '../instance';

const API_URL = '/admin-user';

export const adminUserService = {
  async create(payload: CreateAdminUser): Promise<ApiResponse<AdminUsers>> {
    try {
      const { data } = await api.userApi.post<ApiResponse<AdminUsers>>(`${API_URL}`, payload);
      return data; // { success, message, data }
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error Creating Admin Users');
    }
  },

  async getAll(params?: AdminUserFilterParams): Promise<AdminUserListResponse> {
    try {
      const response = await api.userApi.get<AdminUserListResponse>(`${API_URL}`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error listing all the Admin Users');
    }
  },

  async getUserInfo() {
    try {
      const response = await api.userApi.get<AdminUserInfoResponse>(`${API_URL}/user-count`);
      return response.data.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error listing the Admin Users information');
    }
  },

  async getById(id: number): Promise<AdminSingleUserResponse> {
    try {
      const response = await api.userApi.get<AdminSingleUserResponse>(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error in fetching the single Admin User');
    }
  },

  async createBlueNumber(phoneNumber: string): Promise<BlueNumberResponse> {
    try {
      const response = await api.userApi.post<BlueNumberResponse>(`/blue-number`, {
        phoneNumber,
      });
      return response.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error Creating Admin Users');
    }
  },

  async getPersonalDetails(params?: {
    blueNumberId?: string;
    phoneNumber?: string;
  }): Promise<UserLookupResponse> {
    try {
      const response = await api.userApi.get<UserLookupResponse>(`${API_URL}/user-blue-number`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error getting the details');
    }
  },

  async getBlueNumberDetails(phoneNumber: string) {
    try {
      const blueNumberResponse = await api.userApi.get<BlueNumberResponse>(
        `/blue-number/${phoneNumber}`,
      );
      return blueNumberResponse.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.responseException?.exceptionMessage ||
        error.response?.data?.message ||
        error.message ||
        'Something went wrong';

      showToast.error(errorMsg);
      return null; // optional but recommended
    }
  },

  async getMobileNumberDetails(blueNumberId: string) {
    try {
      const blueNumberResponse = await axios.get(
        `${process.env.REACT_APP_BLUENUMBER_BASE_URL}/api/v1/Connection/${blueNumberId}`,
      );
      const blueNumberData = blueNumberResponse.data;
      return blueNumberData;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.responseException?.exceptionMessage ||
        error.response?.data?.message ||
        error.message ||
        'Something went wrong';

      showToast.error(errorMsg);
      return null; // optional but recommended
    }
  },

  async getAllRoles(): Promise<Roles[]> {
    try {
      const response = await api.userApi.get<ApiResponse<Roles[]>>(`/user-role/filter-drop-down`);
      return response.data.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error in fetching all the roles');
    }
  },

  async update(id: number, payload: UpdateAdminUser): Promise<ApiResponse<AdminUsers>> {
    try {
      const { data } = await api.userApi.put<ApiResponse<AdminUsers>>(`${API_URL}/${id}`, payload);
      return data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error in updating Admin User');
    }
  },

  async remove(id: number): Promise<ApiResponse<any>> {
    try {
      const { data } = await api.userApi.delete<ApiResponse<any>>(`${API_URL}/${id}`);
      return data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error in deleting the  Admin User');
    }
  },

  exportAdminUsers: async () => {
    try {
      const res = await api.userApi.get(`${API_URL}/bulk-export`, {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error exporting Admin Users');
    }
  },
};
